import { useEffect, useState } from "react";
import { documentsService } from "../services/documents";
import { Button } from "./ui/Button";
import type { DocumentHistoryItem, DocumentType, DocumentVersion } from "../types";

interface Props {
  jobId: string;
  docType: DocumentType;
  currentVersionId: string | undefined;
  onRestore: (version: DocumentVersion) => void;
}

export default function ModificationHistory({ jobId, docType, currentVersionId, onRestore }: Props) {
  const [history, setHistory] = useState<DocumentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [jobId, docType]);

  async function loadHistory() {
    setLoading(true);
    try {
      const items = await documentsService.getHistory(jobId, docType);
      setHistory(items);
    } catch {
      setError("Failed to load history.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRestore(versionId: string) {
    setRestoringId(versionId);
    setError(null);
    try {
      const version = await documentsService.restore(jobId, docType, versionId);
      onRestore(version);
      await loadHistory();
    } catch {
      setError("Failed to restore version.");
    } finally {
      setRestoringId(null);
    }
  }

  if (loading) {
    return <p className="text-slate-400 text-sm py-2">Loading history…</p>;
  }

  if (history.length === 0) {
    return <p className="text-slate-400 text-sm py-2">No version history yet.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md px-3.5 py-2.5 mb-2 text-sm text-error">
          {error}
        </div>
      )}
      {history.map((item) => {
        const isCurrent = item.version_id === currentVersionId;
        const date = new Date(item.created_at).toLocaleString();
        return (
          <div
            key={item.version_id}
            className={[
              "flex items-center justify-between px-3.5 py-2.5 rounded-md border",
              isCurrent
                ? "bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800"
                : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700",
            ].join(" ")}
          >
            <div>
              <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                v{item.sequence_number}
              </span>
              <span className="text-slate-400 text-xs ml-2.5">{date}</span>
              {item.instruction === "__generated__" ? (
                <div className="text-slate-400 text-xs mt-0.5">AI generated</div>
              ) : item.instruction ? (
                <div className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                  "{item.instruction}"
                </div>
              ) : (
                <div className="text-slate-400 text-xs mt-0.5">Manual edit</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isCurrent ? (
                <span className="text-xs text-accent font-semibold">Current</span>
              ) : (
                <Button
                  onClick={() => handleRestore(item.version_id)}
                  disabled={restoringId === item.version_id}
                  loading={restoringId === item.version_id}
                  variant="secondary"
                  size="sm"
                >
                  Restore
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
